import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { IModuleContext, IModuleProps } from "./module.interface";
import { IModule, IModuleProgress, IProgress } from "../../interfaces/modules.interface";
import {
    useGetFavoriteModulesIds,
    useGetModule,
    useGetModuleFavorite,
    useGetModuleProgress,
    useGetModules,
    useGetModulesProgress,
    useGetPathwayModules,
    useSetModuleProgress
} from "../../hooks/useModules";
import { TranslationContext } from "../translation/translation.provider";
import { DEFAULT_LANGUAGE, PATHWAYS } from "../../constants";
import { LoadingContext } from "../loading/loading.provider";
import { UserContext } from "../user/user.provider";

const moduleInitValues: IModuleContext = {
    updated: undefined,
    setUpdated: Function.prototype(),
    progress: undefined,
    setProgress: Function.prototype(),
    module: undefined,
    setModule: Function.prototype(),
    modules: [],
    setModules: Function.prototype(),
    pathwayModules: [],
    setPathwayModules: Function.prototype(),
    currentModuleId: undefined,
    setCurrentModuleId: Function.prototype(),
    currentUnitId: undefined,
    setCurrentUnitId: Function.prototype()
};

export const ModuleContext = createContext<IModuleContext>(moduleInitValues);

const ModuleProvider = ({ children }: IModuleProps) => {
    const { lang } = useContext(TranslationContext);
    const { userSettings, handleUserSettings } = useContext(UserContext);
    const { setIsLoading } = useContext(LoadingContext);
    const [updated, setUpdated] = useState<Date>();
    const [progress, setProgress] = useState<IProgress>();
    const [module, setModule] = useState<IModule>();
    const [modules, setModules] = useState<IModule[]>();
    const [pathwayModules, setPathwayModules] = useState<IModule[]>();
    const [currentModuleId, setCurrentModuleId] = useState<string>();
    const [currentUnitId, setCurrentUnitId] = useState<string>();
    const updateProgress = useSetModuleProgress();
    const getFavoriteIds = useGetFavoriteModulesIds();

    // GET THE MODULE FOR THE CURRENT MODULE PAGE
    const { data: moduleData, isLoading: moduleLoading } = useGetModule<IModule>(
        currentModuleId ?? "",
        lang ?? DEFAULT_LANGUAGE,
        null,
        { enabled: !!currentModuleId }
    );

    const getModuleFavorite = useGetModuleFavorite();
    const getModulesProgress = useGetModulesProgress();
    const getModuleProgress = useGetModuleProgress();
    const pathway = useMemo(
        () => (userSettings?.pathway !== undefined ? PATHWAYS[userSettings.pathway] : PATHWAYS[0]),
        [userSettings]
    );

    const { data: pathwayData, isLoading: pathwayLoading } = useGetPathwayModules(pathway);

    const getProgress = useCallback(
        (modulesProgress: IModuleProgress[] | undefined, moduleId: string) => {
            if (!modulesProgress) return 0;

            const moduleProgress = modulesProgress?.find((m) => m.moduleId === moduleId);
            const isCompleted = moduleProgress?.completed;

            if (isCompleted) return 100;

            return moduleProgress && !isCompleted
                ? Math.round((moduleProgress.totalUnitsCompleted / moduleProgress.totalUnits) * 100)
                : 0;
        },
        []
    );

    const {
        refetch: refetchModulesList,
        data: modulesData,
        isLoading: modulesLoading
    } = useGetModules<IModule[]>(lang ?? DEFAULT_LANGUAGE, pathway);

    const handleModuleUpdate = async (moduleData: IModule) => {
        // UPDATES MODULE FAVORITE STATUS, MODULE PROGRESS AND UNIT MODULE PROGRESS
        setIsLoading(true);
        let module = moduleData;
        
        if (!lang) return;

        // FAVORITES
        if (!modules) {
            // IF ACCESSING DIRECTLY TO A MODULE DETAIL PAGE
            // POPULATE MODULES VARIABLE, GET PROGRESS AND FAVORITE STATUS

            const [isFavorite, modulesProgress] = await Promise.all([
                getModuleFavorite.mutateAsync(moduleData.id),
                getModulesProgress.mutateAsync({
                    locale: lang
                })
            ]);

            module = {
                ...module,
                isFavorite,
                moduleProgress: getProgress(modulesProgress, module.id)
            };
        } else {
            module = {
                ...module,
                isFavorite: modules.find((m) => m.id === moduleData.id)?.isFavorite,
                moduleProgress: modules.find((m) => m.id === moduleData.id)?.moduleProgress
            };
        }
        
        const moduleUnitsProgress = await getModuleProgress.mutateAsync({
            moduleId: module.id,
            locale: lang ?? DEFAULT_LANGUAGE
        });

        const units =
            module.units?.map((unit) => {
                const completedItems =
                    moduleUnitsProgress?.units?.find((u) => u.id === unit.id)?.completedItems ?? 0;
                const numberItems = unit.items.length > 0 ? unit.items.length : 1;
                return { ...unit, isCompleted: completedItems >= numberItems };
            }) ?? [];

        module = { ...module, units, unitsProgress: moduleUnitsProgress?.units };

        // PROGRESS
        setModule(module);
        setIsLoading(false);
    };

    const handleModulesListUpdate = async (modulesData: IModule[]) => {
        if (!lang) return;

        setIsLoading(true);
        const responseFavorites = await getFavoriteIds.mutateAsync();

        if (responseFavorites.success) {
            const favorites = responseFavorites.data as string[];

            modulesData = modulesData?.map((mod) => ({
                ...mod,
                isFavorite: favorites.indexOf(mod.id) >= 0
            }));
        }

        const modulesProgress = await getModulesProgress.mutateAsync({
            locale: lang
        });
        
        modulesData = modulesData.map((mod: IModule, i: number) => ({
            ...mod,
            moduleProgress: getProgress(modulesProgress, mod.id)
        }));

        if (pathwayData) {
            const pathwayModules = modulesData
                .sort((a, b) => {
                    return pathwayData?.indexOf(a.id) - pathwayData?.indexOf(b.id);
                })
                .filter((m) => pathwayData.includes(m.id));

            setPathwayModules(pathwayModules);
        }

        setModules(modulesData);
        setIsLoading(false);
    };

    useEffect(() => {
        const isLoading = modulesLoading || moduleLoading || !modules;

        setIsLoading(isLoading);
    }, [modulesLoading, moduleLoading]);

    useEffect(() => {
        // SET THE MODULES LIST COMBINED WITH FAVORITES & PROGRESS
        if (modulesLoading || !modulesData || pathwayLoading) return;

        handleModulesListUpdate(modulesData);
    }, [modulesData, modulesLoading, updated]);

    useEffect(() => {
        // GET THE MODULES LIST AFTER UPDATED ACTION

        refetchModulesList();
    }, [updated]);

    useEffect(() => {
        // SET THE CURRENT MODULE DATA
        if (!moduleData || modulesLoading) return;
        handleModuleUpdate(moduleData);
    }, [moduleData, modulesLoading, pathwayLoading, updated, modules]);

    useEffect(() => {
        // UPDATES MODULE PROGRESS
        if (!module?.id || progress?.progress === 1 || !progress) return;
        console.log("progress updating...", progress);

        updateProgress.mutateAsync({ ...(progress as IProgress), moduleId: module.id });
        handleUserSettings();
    }, [progress]);

    return (
        <ModuleContext.Provider
            value={{
                updated,
                setUpdated,
                progress,
                setProgress,
                module,
                setModule,
                modules,
                setModules,
                pathwayModules,
                setPathwayModules,
                currentModuleId,
                setCurrentModuleId,
                currentUnitId,
                setCurrentUnitId
            }}>
            {children}
        </ModuleContext.Provider>
    );
};

export default ModuleProvider;
